import {
  DEFAULT_RANGE_PRESET,
  computePresetRange,
} from "../lib/dateRangePresets";
import type { GlobalFilters } from "../lib/globalFilters";
import { readSettings } from "../lib/settings";
import type { ApiClient } from "./client";
import { calendarQueryOptions } from "./hooks/useCalendar";
import { drawdownQueryOptions } from "./hooks/useDrawdown";
import { equityCurveQueryOptions } from "./hooks/useEquityCurve";
import { notesQueryOptions } from "./hooks/useNotes";
import { pnlByDayQueryOptions } from "./hooks/usePnLByDay";
import { summaryQueryOptions } from "./hooks/useSummary";
import { tradeGroupsQueryOptions } from "./hooks/useTradeGroups";
import { tradesQueryOptions } from "./hooks/useTrades";

// Primary sidebar destinations whose landing data we can warm ahead of a click
// (hover/idle prefetch) and await before committing a navigation (route
// loaders). Other routes (Analytics, Setup) are not warmed.
export const PREFETCHABLE_ROUTES = [
  "/dashboard",
  "/trades",
  "/calendar",
  "/journal/notes",
] as const;

export type PrefetchableRoute = (typeof PREFETCHABLE_ROUTES)[number];

export function isPrefetchableRoute(route: string): route is PrefetchableRoute {
  return (PREFETCHABLE_ROUTES as readonly string[]).includes(route);
}

// A minimal, callable React Query options shape -- enough for prefetchQuery /
// ensureQueryData without leaking each endpoint's concrete response type.
export interface WarmTarget {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
}

// The exact queries each page renders on its default (no saved-screen) view,
// keyed off the current filters so a warmed entry lines up with what the page
// will subscribe to. A mismatch (e.g. an active saved screen) simply misses and
// the page fetches normally.
export function routeWarmTargets(
  client: ApiClient,
  route: string,
  filters: GlobalFilters,
): WarmTarget[] {
  switch (route) {
    case "/dashboard": {
      // The dashboard blocks first paint on all four core analytics queries;
      // warm them together so the page renders without its skeleton. Mirror the
      // dashboard's month-to-date default so the warmed keys line up with what
      // the page subscribes to when the URL carries no range.
      const range =
        filters.since === undefined && filters.until === undefined
          ? computePresetRange(DEFAULT_RANGE_PRESET, new Date())
          : { since: filters.since, until: filters.until };
      const dashboardFilters: GlobalFilters = {
        ...filters,
        since: range.since,
        until: range.until,
      };
      return [
        summaryQueryOptions(client, dashboardFilters),
        pnlByDayQueryOptions(client, dashboardFilters),
        equityCurveQueryOptions(client, dashboardFilters),
        drawdownQueryOptions(client, dashboardFilters),
      ];
    }
    case "/trades": {
      const openedAfter =
        filters.since !== undefined ? `${filters.since}T00:00:00.000Z` : undefined;
      const openedBefore =
        filters.until !== undefined ? `${filters.until}T23:59:59.999Z` : undefined;
      const dateWindow = {
        ...(openedAfter !== undefined ? { opened_after: openedAfter } : {}),
        ...(openedBefore !== undefined ? { opened_before: openedBefore } : {}),
      };
      // Mirror Trades.tsx defaults: the view shows either the flat list or the
      // scalper roll-up groups (settings-driven), so warm both.
      const display = readSettings().display;
      return [
        tradesQueryOptions(client, {
          limit: 20,
          offset: 0,
          sort_key: "opened_at",
          sort_order: "desc",
          ...dateWindow,
        }),
        tradeGroupsQueryOptions(client, {
          sort_order: "desc",
          window_minutes: display.scalperWindowMinutes,
          min_run: display.scalperMinRun,
          ...dateWindow,
        }),
      ];
    }
    case "/calendar":
      return [calendarQueryOptions(client, filters)];
    case "/journal/notes":
      return [notesQueryOptions(client, {})];
    default:
      return [];
  }
}
